import { RadioGroup } from '@headlessui/react'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { motion, useMotionValue } from 'framer-motion'
import { classes } from 'src/utils'

type Props<T> = {
  options: { key: T; label: string }[]
  activeOption: string
  setActive: (optionKey: T) => void
  className?: string
}

export function SegmentedControl<T extends string>(props: Props<T>) {
  const width = useMotionValue(0)
  const translateX = useMotionValue(0)
  const transitionProperty = useMotionValue('none')
  const parentEl = useRef<HTMLDivElement>(null)
  const activeIndex = useMemo(
    () => props.options.findIndex((item) => item.key === props.activeOption),
    [props.options, props.activeOption],
  )

  useLayoutEffect(() => {
    if (parentEl.current) {
      const activeEl = parentEl.current.children[
        activeIndex + 1
      ] as HTMLDivElement
      width.set(activeEl.offsetWidth)
      translateX.set(activeEl.offsetLeft)
    }
  }, [width, translateX, activeIndex])

  useEffect(() => {
    transitionProperty.set('all')
  }, [transitionProperty])

  return (
    <RadioGroup
      value={props.activeOption}
      onChange={props.setActive}
      ref={parentEl}
      className={classes(
        'flex flex-row items-center rounded-md bg-slate-100 px-1 py-0.5 h-10 outline-none relative z-10',
        props.className,
      )}
    >
      <motion.div
        className="absolute left-0 transition bg-white rounded shadow-sm -z-10 inset-y-1"
        style={{ width, translateX, transitionProperty }}
      />

      {props.options.map((item, index) => (
        <RadioGroup.Option
          key={item.key}
          value={item.key}
          className={({ active, checked }) =>
            classes(
              'text-center h-full flex items-center justify-center text-sm font-medium cursor-pointer rounded focus-visible:ring-theme-500 focus-visible:ring-2 outline-none px-3 capitalize focus-visible:bg-white',
              checked
                ? 'text-slate-700'
                : active
                ? 'text-slate-600 bg-slate-50'
                : 'text-slate-400 hover:text-slate-500',
            )
          }
        >
          {item.label}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
  )
}
